import { createErrorBuilder, BaseError } from '@wix/public-editor-platform-errors';
import { PlatformAppEvent } from '@wix/public-editor-platform-events';

var EditorPlatformApplicationContextErrorCode = /* @__PURE__ */ ((EditorPlatformApplicationContextErrorCode2) => {
  EditorPlatformApplicationContextErrorCode2["IncorrectEnvironment"] = "IncorrectEnvironment";
  EditorPlatformApplicationContextErrorCode2["ClientAuthError"] = "ClientAuthError";
  return EditorPlatformApplicationContextErrorCode2;
})(EditorPlatformApplicationContextErrorCode || {});
class EditorPlatformApplicationContextError extends BaseError {
  state = {};
  constructor(message, code) {
    super(message, code, "Editor Platform Application Context Error");
  }
  withUrl(url) {
    this.state = { ...this.state, url };
    return this;
  }
  withAppDefinitionId(appDefinitionId) {
    this.state = { ...this.state, appDefinitionId };
    return this;
  }
}
const createEditorPlatformApplicationContextError = createErrorBuilder(EditorPlatformApplicationContextError);

async function transformEventPayload(eventPayload, privateAPI) {
  if (!eventPayload?.type) {
    return eventPayload;
  }
  switch (eventPayload.type) {
    case "componentSelectionChanged":
      const componentRefs = eventPayload.componentRefs || [];
      const components = await Promise.all(
        componentRefs.map((ref) => {
          return privateAPI.components.getComponent(ref);
        })
      );
      return {
        type: eventPayload.type,
        components
      };
    default:
      return eventPayload;
  }
}

class ApplicationBoundEvents {
  constructor(appDefinitionId, events, privateAPI) {
    this.appDefinitionId = appDefinitionId;
    this.privateAPI = privateAPI;
    this.events = events;
    this.subscribe = events.subscribe.bind(events);
    this.commit = events.commit.bind(events);
    this.startTransaction = events.startTransaction.bind(events);
    this.silent = events.silent.bind(events);
  }
  events;
  subscribe;
  commit;
  startTransaction;
  silent;
  notify(event) {
    this.events.notify({
      type: event.type,
      payload: event.payload,
      meta: {
        appDefinitionId: this.appDefinitionId
      }
    });
  }
  notifyCustomEvent(type, payload) {
    this.notify({
      type: PlatformAppEvent.CustomEvent,
      payload: {
        ...payload,
        type
      }
    });
  }
  /**
   * TODO: we should use same interface for all events
   * (subscribe vs addEventListener)
   */
  addEventListener(eventType, fn) {
    return this.events.subscribe(async (event) => {
      const isAppMatch = event.meta?.appDefinitionId === this.appDefinitionId || event.meta?.appDefinitionId === null;
      const transformPayload = () => transformEventPayload(event.payload, this.privateAPI);
      if (eventType === "*") {
        fn(await transformPayload());
      } else if (event.type === PlatformAppEvent.CustomEvent) {
        if (eventType === event.payload?.type && !isAppMatch) {
          fn(await transformPayload());
        }
      } else if (event.type === PlatformAppEvent.HostEvent) {
        if (eventType === event.payload?.type && isAppMatch) {
          fn(await transformPayload());
        }
      }
    });
  }
}

const WAIT_INJECTED_TIMEOUT = 200;
const WAIT_INJECTED_RETRY_COUNT = 50;
class ContextInjectionStatus {
  _resolveContextInjected = () => {
  };
  _isInjected = false;
  key;
  constructor(uuid) {
    this.key = `__${uuid}_CONTEXT_INJECTION_STATUS_KEY`;
    if (!globalThis[this.key]) {
      globalThis[this.key] = new Promise((resolve) => {
        this._resolveContextInjected = () => {
          this._isInjected = true;
          resolve();
        };
      });
    }
  }
  isInjected() {
    return !!this._isInjected;
  }
  resolveInjected() {
    this._resolveContextInjected?.();
  }
  waitInjected() {
    return new Promise((resolve, reject) => {
      let injected = false;
      let timeoutId;
      let retryCount = 0;
      const timeout = () => {
        if (injected) {
          return;
        }
        timeoutId = setTimeout(() => {
          retryCount++;
          if (retryCount < WAIT_INJECTED_RETRY_COUNT) {
            if (retryCount % 10 === 0) {
              console.log(
                createEditorPlatformApplicationContextError(
                  EditorPlatformApplicationContextErrorCode.IncorrectEnvironment,
                  "contexts are not resolved, still re-trying"
                ).withMessage(`try number ${retryCount}`).message
              );
            }
            timeout();
            return;
          }
          if (!injected) {
            const error = createEditorPlatformApplicationContextError(
              EditorPlatformApplicationContextErrorCode.IncorrectEnvironment,
              "contexts are not resolved, threw by timeout"
            );
            reject(error);
          }
        }, WAIT_INJECTED_TIMEOUT);
      };
      timeout();
      const _waitContextInjectedPromise = globalThis[this.key];
      _waitContextInjectedPromise.then(() => {
        injected = true;
        clearTimeout(timeoutId);
        resolve();
      });
    });
  }
}

const ENVIRONMENT_CONTEXT_KEY = "__ENVIRONMENT_CONTEXT_KEY";
var PlatformEnvironment = /* @__PURE__ */ ((PlatformEnvironment2) => {
  PlatformEnvironment2["Worker"] = "Worker";
  PlatformEnvironment2["Frame"] = "Frame";
  PlatformEnvironment2["ComponentPanel"] = "ComponentPanel";
  return PlatformEnvironment2;
})(PlatformEnvironment || {});
class EnvironmentContext {
  constructor(environmentContext) {
    this.environmentContext = environmentContext;
  }
  static status = new ContextInjectionStatus("environment");
  static async inject(context) {
    if (globalThis[ENVIRONMENT_CONTEXT_KEY]) {
      throw createEditorPlatformApplicationContextError(
        EditorPlatformApplicationContextErrorCode.IncorrectEnvironment,
        "Environment context already exists and should not be overridden"
      );
    }
    globalThis[ENVIRONMENT_CONTEXT_KEY] = new EnvironmentContext(context);
    this.status.resolveInjected();
  }
  static async getInstance() {
    await this.status.waitInjected();
    return globalThis[ENVIRONMENT_CONTEXT_KEY];
  }
  getPrivateAPI() {
    return this.environmentContext.privateApi;
  }
  getEvents() {
    return this.environmentContext.events;
  }
  getApplicationAPIs() {
    return this.environmentContext.applicationAPIs ?? {};
  }
  getEnvironment() {
    return this.environmentContext.environment;
  }
}

const APPLICATION_CONTEXT_KEY = "__APPLICATION_CONTEXT_KEY";
class ApplicationContext {
  constructor(applicationContext, environment) {
    this.applicationContext = applicationContext;
    this.environment = environment;
    this.events = new ApplicationBoundEvents(
      this.applicationContext.appDefinitionId,
      this.environment.getEvents(),
      this.environment.getPrivateAPI()
    );
  }
  static status = new ContextInjectionStatus("application");
  /**
   * TODO: use generics for context type
   * - application
   * - editor
   */
  static async inject(context) {
    const environment = await EnvironmentContext.getInstance();
    if (environment.getEnvironment() !== PlatformEnvironment.Frame) {
      throw createEditorPlatformApplicationContextError(
        EditorPlatformApplicationContextErrorCode.IncorrectEnvironment,
        "Application context can be injected only in frame environment"
      );
    }
    if (globalThis[APPLICATION_CONTEXT_KEY]) {
      throw createEditorPlatformApplicationContextError(
        EditorPlatformApplicationContextErrorCode.IncorrectEnvironment,
        "Application context already exists and should not be overridden"
      );
    }
    globalThis[APPLICATION_CONTEXT_KEY] = new ApplicationContext(
      context,
      await EnvironmentContext.getInstance()
    );
    this.status.resolveInjected();
  }
  static async getInstance() {
    const environment = await EnvironmentContext.getInstance();
    if (environment.getEnvironment() === PlatformEnvironment.Frame) {
      await this.status.waitInjected();
      return globalThis[APPLICATION_CONTEXT_KEY];
    } else {
      return __APPLICATION_CONTEXT_KEY;
    }
  }
  events;
  getAppDefinitionId() {
    return this.applicationContext.appDefinitionId;
  }
  getBindings() {
    return this.applicationContext;
  }
  getEvents() {
    return this.events;
  }
  getPrivateAPI() {
    return this.environment.getPrivateAPI();
  }
  getPrivateApplicationAPI() {
    const appDefinitionId = this.getAppDefinitionId();
    if (!appDefinitionId) {
      throw createEditorPlatformApplicationContextError(
        EditorPlatformApplicationContextErrorCode.IncorrectEnvironment,
        "appDefinitionId is not available"
      );
    }
    return this.environment.getApplicationAPIs()[appDefinitionId];
  }
}

export { APPLICATION_CONTEXT_KEY, ApplicationBoundEvents, ApplicationContext, ENVIRONMENT_CONTEXT_KEY, EnvironmentContext, PlatformEnvironment };
//# sourceMappingURL=index.js.map
